import { useState } from "react";
import { Box, Grid } from "@mui/material";
// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
// Constants
import actions from "@/Constants/actionsList";
// Components
import SystemSettingsSideBar from "../SystemSettingsSideBar";
import SystemSettingsLoader from "../SystemSettingsLoader";
import CollapseArea from "@/Layouts/components/Basics/CollapseArea";
import Cit from "./sections/Cit";
import CommonDivider from "../common/CommonDivider";
import PVRMaxValues from "./sections/PVRMaxValues";
import PayPeriods from "./sections/PayPeriods";
import MaxPTO from "./sections/PTO";
import MinimumWage from "./sections/MinimumWage";
import CallActivity from "./sections/CallActivity";
import SalesLog from "./sections/SalesLog";
import { RootState } from "@/redux/store";
import { useQueryClient } from "@tanstack/react-query";
import { ISystemSettings } from "@/Settings/types/Common/model";
import { useSelector } from "react-redux";
import handleScroll from "@/Settings/helpers/SystemSettings/handleScroll";
import CrossStore from "./sections/CrossStore";

const menus = [
	{
		label: "CIT",
		elementId: "cit",
	},
	{
		label: "PVR Max Graph Values",
		elementId: "pvrMaxValues",
	},
	{
		label: "Pay Periods Per Commission Cycles",
		elementId: "payPeriodsPerCommissionCycles",
	},
	{
		label: "Max Allowable Borrowed PTO",
		elementId: "maxAllowableBorrowedPTO",
	},
	{
		label: "Minimum Wage",
		elementId: "minimumWage",
	},
	{
		label: "Call Activity Performance",
		elementId: "callActivityPerformance",
	},
	{
		label: "Sales Log",
		elementId: "salesLog",
	},
	{
		label: "Cross Store",
		elementId: "crossStore",
	},
];

const GeneralContainer = () => {
	const [itemSelected, setItemSelected] = useState("cit");
	const { useAuth } = useAuthorization();
	const citMaxValuesActions = useAuth(actions.EDIT_CIT_MAX_VALUES);

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";
	const queryClient = useQueryClient();
	const isLoadingSystemSettings = queryClient.getQueryState(["systemSettings", locationId])?.status === "pending";
	const isLoadingSystemSettingsAdditionalInfo =
		queryClient.getQueryState(["systemSettingsAdditionalInfo", locationId])?.status === "pending";
	const isLoadingAppendingConfigInfo = queryClient.getQueryState(["appendingConfigInfo", locationId])?.status === "pending";
	const systemSettings = queryClient.getQueryData(["systemSettings", locationId]) as ISystemSettings | null;
	const franchisesAmount = location?.franchises?.length || 1;

	const franchises = [
		...(location?.franchises || []).map((franchise, i) => ({
			id: i,
			label: franchise,
		})),
		{
			id: franchisesAmount,
			label: "Pre-Owned",
		},
		{
			id: franchisesAmount + 1,
			label: "Wholesale",
		},
	];

	const handleMenuClick = (param: { label: string; elementId: string }) => {
		setItemSelected(param.elementId);
		handleScroll(param.elementId);
	};

	const handleCollapse = (collapsed: boolean, selected: string) => {
		if (!collapsed) {
			setItemSelected(selected);
			handleScroll(selected);
		}
	};

	const isLoading = isLoadingSystemSettings || isLoadingSystemSettingsAdditionalInfo || isLoadingAppendingConfigInfo;

	return (
		<Grid container className="mt-[32px] mb-[48px]">
			<Grid item xs={2}>
				<SystemSettingsSideBar menus={menus} onActionClick={handleMenuClick} selected={itemSelected} loading={isLoading} />
			</Grid>
			<Grid item xs={10}>
				<Grid className="w-full ml-[30px]">
					{isLoading || !locationId ? (
						<SystemSettingsLoader />
					) : (
						<>
							<Box id="cit">
								<CollapseArea
									title="CIT"
									openCollapse={itemSelected === "cit"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "cit")}
									sx={{ padding: "0px 30px" }}
								>
									<Cit action={citMaxValuesActions} locationId={locationId} systemSettings={systemSettings} />
								</CollapseArea>
							</Box>
							<CommonDivider />
							<Box id="pvrMaxValues">
								<CollapseArea
									title="PVR Max Graph Values"
									openCollapse={itemSelected === "pvrMaxValues"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "pvrMaxValues")}
									sx={{ padding: "0px 30px" }}
								>
									<PVRMaxValues locationId={locationId} systemSettings={systemSettings} />
								</CollapseArea>
							</Box>
							<CommonDivider />
							<Box id="payPeriodsPerCommissionCycles">
								<CollapseArea
									title="Pay Periods per Commission Cycles"
									openCollapse={itemSelected === "payPeriodsPerCommissionCycles"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "payPeriodsPerCommissionCycles")}
									sx={{ padding: "0px 30px" }}
								>
									<PayPeriods
										locationId={locationId}
										commissionCyclesPerMonth={systemSettings?.commissionCyclesPerMonth}
									/>
								</CollapseArea>
							</Box>
							<CommonDivider />
							<Box id="maxAllowableBorrowedPTO">
								<CollapseArea
									title="Max Allowable Borrowed PTO"
									openCollapse={itemSelected === "maxAllowableBorrowedPTO"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "maxAllowableBorrowedPTO")}
									sx={{ padding: "0px 30px" }}
								>
									<MaxPTO locationId={locationId} maxAllowableBorrowedPTO={systemSettings?.maxAllowableBorrowedPTO} />
								</CollapseArea>
							</Box>
							<CommonDivider />
							<Box id="minimumWage">
								<CollapseArea
									title="Minimum Wage"
									openCollapse={itemSelected === "minimumWage"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "minimumWage")}
									sx={{ padding: "0px 30px" }}
								>
									<MinimumWage locationId={locationId} minimumWage={systemSettings?.minimumWage} />
								</CollapseArea>
							</Box>
							<CommonDivider />
							<Box id="callActivityPerformance">
								<CollapseArea
									title="Call Activity Performance"
									openCollapse={itemSelected === "callActivityPerformance"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "callActivityPerformance")}
									sx={{ padding: "0px 30px" }}
								>
									<CallActivity
										locationId={locationId}
										callActivityPerformance={systemSettings?.callActivityPerformance}
										phoneNumbers={systemSettings?.blacklistPhoneNumbers}
									/>
								</CollapseArea>
							</Box>
							<CommonDivider />
							<Box id="salesLog">
								<CollapseArea
									title="Sales Log"
									openCollapse={itemSelected === "salesLog"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "salesLog")}
									sx={{ padding: "0px 30px" }}
								>
									<SalesLog
										locationId={locationId}
										systemSettings={systemSettings as ISystemSettings}
										mappedPositions={systemSettings?.mappedPositions}
										franchisesList={franchises}
									/>
								</CollapseArea>
							</Box>
							<CommonDivider />
							<Box id="crossStore">
								<CollapseArea
									title="Cross Store"
									openCollapse={itemSelected === "crossStore"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "crossStore")}
									sx={{ padding: "0px 30px" }}
								>
									<CrossStore
										locationId={locationId}
										crossStoreEmailDistribution={systemSettings?.crossStoreEmailDistribution}
									/>
								</CollapseArea>
							</Box>
						</>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default GeneralContainer;
